import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SubscriptionToggle } from '@/components/wizard/SubscriptionToggle'
import { SubscriptionOfferCard } from '@/components/wizard/SubscriptionOfferCard'
import { StructureOfferCard } from '@/components/wizard/StructureOfferCard'
import { StructureOfferCardSkeleton } from '@/components/wizard/StructureOfferCardSkeleton'
import { RPPSVerificationStep } from '@/components/wizard/RPPSVerificationStep'
import { FINESSVerificationStep } from '@/components/wizard/FINESSVerificationStep'
import { CustomOfferDetailsStep } from '@/components/wizard/CustomOfferDetailsStep'
import { AccountCreationStep } from '@/components/wizard/AccountCreationStep'
import {
  WizardStep,
  SubscriptionInterval,
  OfferType,
  SUBSCRIPTION_OFFERS,
} from '@/constants/wizard'
import { type AdherentStructure } from '@/constants/structures'
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices'
import { useStructureSubscriptions } from '@/hooks/useStructureSubscriptions'
import { SubscriptionType } from '@/types/subscription'
import { RppsFunction } from '@/constants/account'
import { STRIPE_INTERVAL } from '@/constants/subscription'
import { ROUTES } from '@/constants/routes'
import { formatPriceFromCents } from '@/utils/pricing'

function getStructureOfferRowSizes(count: number): number[] {
  if (count <= 4) return [count]
  if (count === 5) return [3, 2]
  if (count === 6) return [3, 3]
  const r = count % 4
  if (r === 0) return Array(count / 4).fill(4)
  if (r === 1) return [...Array(Math.floor((count - 5) / 4)).fill(4), 3, 2]
  if (r === 2) return [...Array(Math.floor((count - 2) / 4)).fill(4), 2]
  return [...Array(Math.floor((count - 3) / 4)).fill(4), 3]
}

export function SubscriptionWizardPage() {
  const { t } = useTranslation('wizard')
  const navigate = useNavigate()
  const { planId } = useParams<{ planId: string }>()
  const isStructurePlan = planId === SubscriptionType.STRUCTURE

  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.OFFER_SELECTION)
  const [interval, setInterval] = useState<SubscriptionInterval>(SubscriptionInterval.MONTHLY)
  const [selectedOffer, setSelectedOffer] = useState<OfferType | null>(null)
  const [selectedStructureOfferId, setSelectedStructureOfferId] = useState<string | null>(null)
  const [rppsNumber, setRppsNumber] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [promoCodeFreeMonths, setPromoCodeFreeMonths] = useState<number | null>(null)
  const [selectedStructure, setSelectedStructure] = useState<AdherentStructure | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [completedSteps, setCompletedSteps] = useState<WizardStep[]>([])
  const [rppsData, setRppsData] = useState<{
    nom_exercice: string
    prenom_exercice: string
    function: string | null
  } | null>(null)
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)
  const [finessNumber, setFinessNumber] = useState('')
  const [facilityData, setFacilityData] = useState<{
    finess_number: string
    facility_name: string
    organization_legal_name?: string
  } | null>(null)
  const [structureFirstName, setStructureFirstName] = useState('')
  const [structureLastName, setStructureLastName] = useState('')
  const [isQuoteFlow, setIsQuoteFlow] = useState(false)
  const [isCustomOfferFlow, setIsCustomOfferFlow] = useState(false)
  const [quoteAddress, setQuoteAddress] = useState('')
  const [quoteCity, setQuoteCity] = useState('')
  const [quotePostalCode, setQuotePostalCode] = useState('')
  const [quotePhone, setQuotePhone] = useState('')
  const [desiredLicensesCount, setDesiredLicensesCount] = useState('')
  const [desiredBillingDetails, setDesiredBillingDetails] = useState('')
  const [registrationCode, setRegistrationCode] = useState('')
  const [foreignFirstName, setForeignFirstName] = useState('')
  const [foreignLastName, setForeignLastName] = useState('')
  const [foreignCountry, setForeignCountry] = useState('')

  const handleBack = () => {
    if (currentStep === WizardStep.OFFER_SELECTION) {
      navigate(ROUTES.HOME)
    } else if (currentStep === WizardStep.ACCOUNT_CREATION && isCustomOfferFlow) {
      setCurrentStep(WizardStep.CUSTOM_OFFER_DETAILS)
    } else if (currentStep === WizardStep.ACCOUNT_CREATION && !isCustomOfferFlow) {
      setCurrentStep(WizardStep.RPPS_VERIFICATION)
    } else if (currentStep === WizardStep.CUSTOM_OFFER_DETAILS) {
      setCurrentStep(WizardStep.RPPS_VERIFICATION)
    } else {
      const currentIndex = Object.values(WizardStep).indexOf(currentStep)
      if (currentIndex > 0) {
        const previousStep = Object.values(WizardStep)[currentIndex - 1]
        setCurrentStep(previousStep)
      }
    }
  }

  const internPrices = useSubscriptionPrices({
    offerType: OfferType.INTERN,
  })
  const practitionerPrices = useSubscriptionPrices({
    offerType: OfferType.PRACTITIONER,
  })
  const memberPrices = useSubscriptionPrices({
    offerType: OfferType.MEMBER,
  })
  const structureSubscriptions = useStructureSubscriptions()

  const getPriceForOffer = (offerId: OfferType, targetInterval: SubscriptionInterval) => {
    const prices =
      offerId === OfferType.INTERN
        ? internPrices.prices
        : offerId === OfferType.PRACTITIONER
          ? practitionerPrices.prices
          : offerId === OfferType.MEMBER
            ? memberPrices.prices
            : []

    const targetIntervalStr =
      targetInterval === SubscriptionInterval.MONTHLY ? STRIPE_INTERVAL.MONTH : STRIPE_INTERVAL.YEAR
    return prices.find(p => p.interval === targetIntervalStr) || prices[0]
  }

  const getPriceForFunction = (
    rppsFunction: string,
    targetInterval: SubscriptionInterval
  ): number | null => {
    const functionLower = rppsFunction.toLowerCase()
    const prices =
      functionLower === RppsFunction.INTERN ? internPrices.prices : practitionerPrices.prices
    const targetIntervalStr =
      targetInterval === SubscriptionInterval.MONTHLY ? STRIPE_INTERVAL.MONTH : STRIPE_INTERVAL.YEAR
    const price = prices.find(p => p.interval === targetIntervalStr) || prices[0]
    return price ? price.amount / 100 : null
  }

  const handleOfferSelect = (offerId: OfferType) => {
    setSelectedOffer(offerId)
    setRegistrationCode('')
    setForeignFirstName('')
    setForeignLastName('')
    setForeignCountry('')
    const price = getPriceForOffer(offerId, interval)
    if (price) {
      setSelectedPriceId(price.id)
    }
    if (!completedSteps.includes(WizardStep.OFFER_SELECTION)) {
      setCompletedSteps([...completedSteps, WizardStep.OFFER_SELECTION])
    }
    setCurrentStep(WizardStep.RPPS_VERIFICATION)
  }

  const handleStructureOfferSelect = (offerId: string, priceId: string) => {
    setIsQuoteFlow(false)
    setIsCustomOfferFlow(false)
    setSelectedStructureOfferId(offerId)
    setSelectedPriceId(priceId)
    if (!completedSteps.includes(WizardStep.OFFER_SELECTION)) {
      setCompletedSteps([...completedSteps, WizardStep.OFFER_SELECTION])
    }
    setCurrentStep(WizardStep.RPPS_VERIFICATION)
  }

  const handleGetQuote = (offerId: string, priceId: string) => {
    setIsQuoteFlow(true)
    setIsCustomOfferFlow(false)
    setSelectedStructureOfferId(offerId)
    setSelectedPriceId(priceId)
    if (!completedSteps.includes(WizardStep.OFFER_SELECTION)) {
      setCompletedSteps([...completedSteps, WizardStep.OFFER_SELECTION])
    }
    setCurrentStep(WizardStep.RPPS_VERIFICATION)
  }

  const handleCustomOfferContact = () => {
    setIsCustomOfferFlow(true)
    setIsQuoteFlow(false)
    setSelectedStructureOfferId('custom-offer')
    setSelectedPriceId(null)
    if (!completedSteps.includes(WizardStep.OFFER_SELECTION)) {
      setCompletedSteps([...completedSteps, WizardStep.OFFER_SELECTION])
    }
    setCurrentStep(WizardStep.RPPS_VERIFICATION)
  }

  const handleRppsVerified = (data: {
    nom_exercice: string
    prenom_exercice: string
    function: string | null
  }) => {
    setRppsData(data)

    if (selectedOffer === OfferType.MEMBER) {
      return
    }

    const functionLower = (data.function ?? '').toLowerCase()
    const correctOfferType =
      functionLower === RppsFunction.INTERN ? OfferType.INTERN : OfferType.PRACTITIONER

    if (
      correctOfferType !== selectedOffer &&
      selectedOffer === OfferType.INTERN &&
      correctOfferType === OfferType.PRACTITIONER
    ) {
      setSelectedOffer(correctOfferType)
      const price = getPriceForOffer(correctOfferType, interval)
      if (price) {
        setSelectedPriceId(price.id)
      }
    }
  }

  const handleNextStep = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep])
    }
    if (currentStep === WizardStep.RPPS_VERIFICATION && isCustomOfferFlow) {
      setCurrentStep(WizardStep.CUSTOM_OFFER_DETAILS)
    } else if (currentStep === WizardStep.RPPS_VERIFICATION) {
      setCurrentStep(WizardStep.ACCOUNT_CREATION)
    } else if (currentStep === WizardStep.CUSTOM_OFFER_DETAILS) {
      setCurrentStep(WizardStep.ACCOUNT_CREATION)
    } else {
      const currentIndex = Object.values(WizardStep).indexOf(currentStep)
      if (currentIndex < Object.values(WizardStep).length - 1) {
        const nextStep = Object.values(WizardStep)[currentIndex + 1]
        setCurrentStep(nextStep)
      }
    }
  }

  const getStepTitle = () => {
    switch (currentStep) {
      case WizardStep.OFFER_SELECTION:
        return isStructurePlan ? t('title') : t('offerSelectionTitle')
      case WizardStep.RPPS_VERIFICATION:
        return isStructurePlan ? t('finessVerification.title') : t('rppsVerification.title')
      case WizardStep.CUSTOM_OFFER_DETAILS:
        return t('customOfferForm.title')
      case WizardStep.ACCOUNT_CREATION:
        return t('accountCreation.title')
      default:
        return t('title')
    }
  }

  const getStepSubtitle = () => {
    switch (currentStep) {
      case WizardStep.OFFER_SELECTION:
        return interval === SubscriptionInterval.MONTHLY
          ? t('intervalSubtitle.monthly')
          : t('intervalSubtitle.yearly')
      case WizardStep.RPPS_VERIFICATION:
        return isStructurePlan ? t('finessVerification.subtitle') : t('rppsVerification.subtitle')
      case WizardStep.CUSTOM_OFFER_DETAILS:
        return t('customOfferForm.description')
      case WizardStep.ACCOUNT_CREATION:
        return t('accountCreation.subtitle')
      default:
        return t('subtitle')
    }
  }

  const structureOfferPrice = useMemo(() => {
    if (
      !selectedStructureOfferId ||
      selectedStructureOfferId === 'custom-offer' ||
      structureSubscriptions.offers.length === 0
    )
      return null
    const offer = structureSubscriptions.offers.find(o => o.id === selectedStructureOfferId)
    const price =
      interval === SubscriptionInterval.MONTHLY ? offer?.prices.month : offer?.prices.year
    const fallback =
      interval === SubscriptionInterval.MONTHLY ? offer?.prices.year : offer?.prices.month
    const p = price ?? fallback
    return p ? p.amount / 100 : null
  }, [selectedStructureOfferId, interval, structureSubscriptions.offers])

  const selectedStructureOfferName = useMemo(() => {
    if (!selectedStructureOfferId || structureSubscriptions.offers.length === 0) return null
    const offer = structureSubscriptions.offers.find(o => o.id === selectedStructureOfferId)
    return offer?.name ?? null
  }, [selectedStructureOfferId, structureSubscriptions.offers])

  const individualOfferPrice = useMemo(() => {
    if (!selectedOffer) return null
    const prices =
      selectedOffer === OfferType.INTERN
        ? internPrices.prices
        : selectedOffer === OfferType.PRACTITIONER
          ? practitionerPrices.prices
          : memberPrices.prices
    const targetIntervalStr =
      interval === SubscriptionInterval.MONTHLY ? STRIPE_INTERVAL.MONTH : STRIPE_INTERVAL.YEAR
    const price = prices.find(p => p.interval === targetIntervalStr) || prices[0]
    return price ? price.amount / 100 : null
  }, [selectedOffer, interval, internPrices.prices, practitionerPrices.prices, memberPrices.prices])

  const sortedIndividualOffers = useMemo(() => {
    const targetIntervalStr =
      interval === SubscriptionInterval.MONTHLY ? STRIPE_INTERVAL.MONTH : STRIPE_INTERVAL.YEAR
    const getAmount = (offer: (typeof SUBSCRIPTION_OFFERS)[number]) => {
      const prices =
        offer.id === OfferType.INTERN
          ? internPrices.prices
          : offer.id === OfferType.PRACTITIONER
            ? practitionerPrices.prices
            : memberPrices.prices
      const price = prices.find(p => p.interval === targetIntervalStr) || prices[0]

      return (
        price?.amount ??
        (interval === SubscriptionInterval.MONTHLY
          ? offer.monthlyPrice * 100
          : offer.yearlyPrice * 100)
      )
    }
    return [...SUBSCRIPTION_OFFERS].sort((a, b) => getAmount(a) - getAmount(b))
  }, [interval, internPrices.prices, practitionerPrices.prices, memberPrices.prices])

  const sortedStructureOffers = useMemo(() => {
    const offers = structureSubscriptions.offers
    const standard = offers.filter(o => o.id !== 'custom-offer')
    const custom = offers.find(o => o.id === 'custom-offer')
    const getAmount = (offer: (typeof offers)[number]) => {
      const price =
        interval === SubscriptionInterval.MONTHLY ? offer.prices.month : offer.prices.year
      const fallback =
        interval === SubscriptionInterval.MONTHLY ? offer.prices.year : offer.prices.month
      return (price ?? fallback)?.amount ?? Infinity
    }
    standard.sort((a, b) => getAmount(a) - getAmount(b))
    return custom ? [...standard, custom] : standard
  }, [structureSubscriptions.offers, interval])

  const effectiveIndividualPriceId = useMemo(() => {
    if (isStructurePlan || rppsData?.function == null || rppsData.function === '')
      return selectedPriceId
    if (selectedOffer === OfferType.MEMBER) return selectedPriceId
    const functionLower = rppsData.function.toLowerCase()
    const isRppsIntern = functionLower === RppsFunction.INTERN
    if (selectedOffer === OfferType.PRACTITIONER && isRppsIntern) {
      return selectedPriceId
    }
    const offerType = isRppsIntern ? OfferType.INTERN : OfferType.PRACTITIONER
    const targetIntervalStr =
      interval === SubscriptionInterval.MONTHLY ? STRIPE_INTERVAL.MONTH : STRIPE_INTERVAL.YEAR
    const prices = offerType === OfferType.INTERN ? internPrices.prices : practitionerPrices.prices
    const price = prices.find(p => p.interval === targetIntervalStr) || prices[0]
    return price?.id ?? selectedPriceId
  }, [
    isStructurePlan,
    rppsData,
    interval,
    selectedOffer,
    selectedPriceId,
    internPrices.prices,
    practitionerPrices.prices,
  ])

  function getIndividualOfferCardProps(offer: (typeof SUBSCRIPTION_OFFERS)[number]) {
    const price = getPriceForOffer(offer.id, interval)
    const isLoading =
      offer.id === OfferType.INTERN
        ? internPrices.isLoading
        : offer.id === OfferType.PRACTITIONER
          ? practitionerPrices.isLoading
          : memberPrices.isLoading
    const displayPrice = !isLoading && price ? formatPriceFromCents(price.amount) : undefined
    return { price, isLoading, displayPrice }
  }

  return (
    <div className="bg-background flex min-h-0 flex-1 flex-col">
      <div className="mt-2 flex shrink-0 items-center px-4 py-4">
        <div className="mx-auto w-full max-w-360">
          <button
            type="button"
            onClick={handleBack}
            className="text-foreground flex cursor-pointer items-center gap-2 transition-opacity hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">{t('buttons.previous')}</span>
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-10">
        <div className="mx-auto w-full max-w-360">
          <div className="mx-auto max-w-4xl">
            <div className="mb-10 text-center">
              <h1 className="text-foreground mb-4 text-xl font-bold">{getStepTitle()}</h1>
              {getStepSubtitle() &&
                currentStep !== WizardStep.OFFER_SELECTION &&
                (currentStep === WizardStep.RPPS_VERIFICATION && !isStructurePlan ? (
                  <div className="text-neutral-gray mx-auto max-w-[578px] text-base font-normal">
                    <p>{t('rppsVerification.subtitleLine1')}</p>
                    <p>{t('rppsVerification.subtitleLine2')}</p>
                  </div>
                ) : (
                  <p className="text-neutral-gray text-base font-normal">{getStepSubtitle()}</p>
                ))}
            </div>

            {currentStep === WizardStep.OFFER_SELECTION && (
              <>
                <div className="mb-4 flex justify-center">
                  <SubscriptionToggle interval={interval} onIntervalChange={setInterval} />
                </div>
                <p className="text-neutral-gray mb-6 text-center text-base font-normal whitespace-pre-line">
                  {interval === SubscriptionInterval.MONTHLY
                    ? t('intervalSubtitle.monthly')
                    : t('intervalSubtitle.yearly')}
                </p>
                {isStructurePlan ? (
                  <div className="flex flex-col items-center gap-4">
                    {structureSubscriptions.isLoading ? (
                      <div className="grid grid-cols-1 gap-4 *:min-h-70.25 md:grid-cols-3 md:items-stretch">
                        {[1, 2, 3].map(i => (
                          <StructureOfferCardSkeleton key={i} showGetQuoteLink />
                        ))}
                        <StructureOfferCardSkeleton showGetQuoteLink={false} />
                      </div>
                    ) : (
                      (() => {
                        const offers = sortedStructureOffers
                        const rowSizes = getStructureOfferRowSizes(offers.length)
                        let offset = 0
                        return rowSizes.map((rowSize, rowIndex) => {
                          const rowOffers = offers.slice(offset, offset + rowSize)
                          offset += rowSize
                          return (
                            <div key={rowIndex} className="flex flex-wrap justify-center gap-4">
                              {rowOffers.map(offer => {
                                const isSelected = selectedStructureOfferId === offer.id
                                const isCompleted =
                                  isSelected && completedSteps.includes(WizardStep.OFFER_SELECTION)
                                const priceId =
                                  interval === SubscriptionInterval.MONTHLY
                                    ? (offer.prices.month?.id ?? offer.prices.year?.id ?? '')
                                    : (offer.prices.year?.id ?? offer.prices.month?.id ?? '')

                                return (
                                  <div key={offer.id} className="w-full shrink-0 md:w-53">
                                    <StructureOfferCard
                                      offer={offer}
                                      interval={interval}
                                      isSelected={isSelected}
                                      isCompleted={isCompleted}
                                      isLoading={false}
                                      maxLicensesFromOffers={
                                        structureSubscriptions.maxLicensesFromOffers
                                      }
                                      onSelect={() =>
                                        offer.id === 'custom-offer'
                                          ? handleCustomOfferContact()
                                          : priceId
                                            ? handleStructureOfferSelect(offer.id, priceId)
                                            : undefined
                                      }
                                      onGetQuote={() =>
                                        priceId ? handleGetQuote(offer.id, priceId) : undefined
                                      }
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })
                      })()
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center gap-3.5 md:flex-nowrap">
                    {sortedIndividualOffers.map((offer: (typeof SUBSCRIPTION_OFFERS)[number]) => {
                      const { price, isLoading, displayPrice } = getIndividualOfferCardProps(offer)
                      return (
                        <SubscriptionOfferCard
                          key={offer.id}
                          offerId={offer.id}
                          interval={interval}
                          price={displayPrice}
                          priceId={price?.id}
                          productName={price?.productName ?? undefined}
                          productDescription={price?.productDescription ?? undefined}
                          isSelected={selectedOffer === offer.id}
                          isCompleted={
                            selectedOffer === offer.id &&
                            completedSteps.includes(WizardStep.OFFER_SELECTION)
                          }
                          isLoading={isLoading}
                          onSelect={() => handleOfferSelect(offer.id)}
                        />
                      )
                    })}
                  </div>
                )}
              </>
            )}

            {currentStep === WizardStep.RPPS_VERIFICATION && isStructurePlan && (
              <FINESSVerificationStep
                finessNumber={finessNumber}
                onFinessNumberChange={setFinessNumber}
                price={structureOfferPrice}
                selectedOfferName={selectedStructureOfferName}
                onNext={handleNextStep}
                onFinessVerified={data => {
                  setFacilityData(data)
                }}
                isQuoteFlow={isQuoteFlow && !isCustomOfferFlow}
                isCustomOfferFlow={isCustomOfferFlow}
                quoteAddress={quoteAddress}
                quoteCity={quoteCity}
                quotePostalCode={quotePostalCode}
                quotePhone={quotePhone}
                onQuoteAddressChange={setQuoteAddress}
                onQuoteCityChange={setQuoteCity}
                onQuotePostalCodeChange={setQuotePostalCode}
                onQuotePhoneChange={setQuotePhone}
              />
            )}

            {currentStep === WizardStep.CUSTOM_OFFER_DETAILS && (
              <CustomOfferDetailsStep
                desiredLicensesCount={desiredLicensesCount}
                desiredBillingDetails={desiredBillingDetails}
                onDesiredLicensesCountChange={setDesiredLicensesCount}
                onDesiredBillingDetailsChange={setDesiredBillingDetails}
                onNext={handleNextStep}
              />
            )}

            {currentStep === WizardStep.RPPS_VERIFICATION && !isStructurePlan && (
              <RPPSVerificationStep
                rppsNumber={rppsNumber}
                onRppsNumberChange={setRppsNumber}
                promoCode={promoCode}
                onPromoCodeChange={setPromoCode}
                onPromoCodeVerified={setPromoCodeFreeMonths}
                selectedOffer={selectedOffer}
                selectedStructure={selectedStructure}
                onStructureChange={setSelectedStructure}
                onNext={handleNextStep}
                onRppsVerified={handleRppsVerified}
                onRegistrationCodeVerified={code => setRegistrationCode(code)}
                price={individualOfferPrice}
                getPriceForFunction={(rppsFunction, targetInterval) => {
                  return getPriceForFunction(rppsFunction, targetInterval)
                }}
                interval={interval}
              />
            )}

            {currentStep === WizardStep.ACCOUNT_CREATION &&
              (() => {
                const isRegistrationCodeFlow = !isStructurePlan && !!registrationCode
                return (
                  <AccountCreationStep
                    rppsNumber={isStructurePlan ? finessNumber : rppsNumber}
                    promoCode={promoCode}
                    promoCodeFreeMonths={promoCodeFreeMonths}
                    email={email}
                    password={password}
                    confirmPassword={confirmPassword}
                    lastName={
                      isStructurePlan
                        ? structureLastName
                        : isRegistrationCodeFlow
                          ? foreignLastName
                          : rppsData?.nom_exercice || ''
                    }
                    firstName={
                      isStructurePlan
                        ? structureFirstName
                        : isRegistrationCodeFlow
                          ? foreignFirstName
                          : rppsData?.prenom_exercice || ''
                    }
                    selectedOffer={selectedOffer}
                    selectedStructureId={selectedStructure?.id || null}
                    rppsFunction={rppsData?.function || ''}
                    selectedPriceId={
                      isStructurePlan
                        ? selectedPriceId
                        : (effectiveIndividualPriceId ?? selectedPriceId)
                    }
                    finessNumber={facilityData?.finess_number}
                    facilityName={facilityData?.facility_name}
                    organizationLegalName={facilityData?.organization_legal_name}
                    isStructurePlan={isStructurePlan}
                    isQuoteFlow={isQuoteFlow}
                    isCustomOfferFlow={isCustomOfferFlow}
                    quoteAddress={quoteAddress}
                    quoteCity={quoteCity}
                    quotePostalCode={quotePostalCode}
                    quotePhone={quotePhone}
                    desiredLicensesCount={desiredLicensesCount}
                    desiredBillingDetails={desiredBillingDetails}
                    registrationCode={isRegistrationCodeFlow ? registrationCode : undefined}
                    country={isRegistrationCodeFlow ? foreignCountry : undefined}
                    onEmailChange={setEmail}
                    onPasswordChange={setPassword}
                    onConfirmPasswordChange={setConfirmPassword}
                    onLastNameChange={
                      isStructurePlan
                        ? setStructureLastName
                        : isRegistrationCodeFlow
                          ? setForeignLastName
                          : undefined
                    }
                    onFirstNameChange={
                      isStructurePlan
                        ? setStructureFirstName
                        : isRegistrationCodeFlow
                          ? setForeignFirstName
                          : undefined
                    }
                    onCountryChange={isRegistrationCodeFlow ? setForeignCountry : undefined}
                    onNext={handleNextStep}
                  />
                )
              })()}
          </div>
        </div>
      </div>
    </div>
  )
}
